import React from 'react';
import {
  Box, Typography, Paper, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, Card, CardContent,
  Divider, Alert, Stack, CircularProgress,
  useTheme, alpha,
} from '@mui/material';
import { CheckCircleOutline, ArrowDownward, WarningAmber } from '@mui/icons-material';
import {
  BusinessPlanFromAPI, PlanQuotas,
  PLAN_QUOTAS, QUOTA_DISPLAY, formatPrice,
} from './types';

interface DowngradeRoute {
  plan: BusinessPlanFromAPI;
  cycle: 'monthly' | 'annual';
}

interface DowngradeSectionProps {
  hasAnnualCommitment: boolean;
  downgradePlans: DowngradeRoute[];
  selectedDowngradePlan: string | null;
  setSelectedDowngradePlan: (v: string | null) => void;
  selectedDowngradeRoute: DowngradeRoute | null;
  currentQuotas: PlanQuotas;
  currentPlanDef: BusinessPlanFromAPI | null;
  currencySymbol: string;
  downgradeLoading: boolean;
  getPrice: (plan: BusinessPlanFromAPI, cycle: 'monthly' | 'annual') => number;
  onShowDowngradeConfirm: () => void;
}

export const DowngradeSection: React.FC<DowngradeSectionProps> = ({
  hasAnnualCommitment, downgradePlans, selectedDowngradePlan,
  setSelectedDowngradePlan, selectedDowngradeRoute, currentQuotas,
  currentPlanDef, currencySymbol, downgradeLoading,
  getPrice, onShowDowngradeConfirm,
}) => {
  const theme = useTheme();

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        You can downgrade to a lower plan. New pricing takes effect from the next billing cycle.
      </Typography>

      {hasAnnualCommitment && (
        <Alert severity="warning" icon={<WarningAmber />} sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>Annual Commitment Notice</Typography>
          <Typography variant="body2">
            Your account is on a 12-month annual commitment. An early termination fee of 20% of the
            annual plan cost may apply if more than 3 months remain in your commitment period.
          </Typography>
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
        {downgradePlans.map(({ plan, cycle }) => {
          const isSelected = selectedDowngradePlan === plan.key;
          const price = getPrice(plan, cycle);
          return (
            <Card
              key={`${plan.key}_${cycle}`} variant="outlined"
              sx={{
                flex: 1, cursor: 'pointer',
                borderColor: isSelected ? 'warning.main' : 'divider',
                borderWidth: isSelected ? 2 : 1,
                bgcolor: isSelected ? alpha(theme.palette.warning.main, 0.04) : 'background.paper',
                transition: 'all 0.2s',
                '&:hover': { borderColor: 'warning.light', boxShadow: 2 },
              }}
              onClick={() => setSelectedDowngradePlan(plan.key)}
            >
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6" fontWeight={700}>{plan.label}</Typography>
                  <Chip label={cycle === 'annual' ? 'Annual' : 'Monthly'} size="small" variant="outlined" />
                </Stack>
                <Typography variant="caption" color="text.secondary">{plan.tagline}</Typography>
                <Typography variant="h5" fontWeight={700} sx={{ mt: 1.5, mb: 0.5 }}>
                  {formatPrice(price, currencySymbol)}
                  <Typography component="span" variant="body2" color="text.secondary">/month</Typography>
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                  {plan.noOfUsers} user{plan.noOfUsers > 1 ? 's' : ''}
                </Typography>
                {plan.highlights.map((h, idx) => (
                  <Box key={idx}>
                    {h.features.map((f, fi) => (
                      <Typography key={fi} variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CheckCircleOutline sx={{ fontSize: 14, color: 'text.disabled' }} /> {f}
                      </Typography>
                    ))}
                  </Box>
                ))}
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      {selectedDowngradeRoute && (
        <>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>What You'll Lose</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.warning.main, 0.06) }}>
                  <TableCell sx={{ fontWeight: 700 }}>Feature</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    {currentPlanDef?.label || 'Current'}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: 'warning.main' }}>
                    {selectedDowngradeRoute.plan.label}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Change</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {QUOTA_DISPLAY.map(({ key, label }) => {
                  const currentVal = currentQuotas[key];
                  const targetPlan = downgradePlans.find(dp => dp.plan.key === selectedDowngradePlan)?.plan;
                  const newVal = targetPlan?.quotas?.[key] ?? PLAN_QUOTAS[selectedDowngradePlan!]?.[key] ?? 0;
                  const diff = newVal - currentVal;
                  return (
                    <TableRow key={key}>
                      <TableCell>{label}</TableCell>
                      <TableCell align="center">{currentVal === 0 ? '—' : currentVal.toLocaleString()}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>{newVal === 0 ? '—' : newVal.toLocaleString()}</TableCell>
                      <TableCell align="center">
                        {diff < 0 ? (
                          <Chip label={diff.toLocaleString()} size="small" color="error" variant="outlined" />
                        ) : diff === 0 ? '—' : (
                          <Chip label={`+${diff.toLocaleString()}`} size="small" color="success" variant="outlined" />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Button
            variant="outlined" color="warning" size="large"
            startIcon={downgradeLoading ? <CircularProgress size={20} /> : <ArrowDownward />}
            disabled={downgradeLoading}
            onClick={onShowDowngradeConfirm}
            sx={{ px: 4 }}
          >
            {downgradeLoading
              ? 'Processing...'
              : `Downgrade to ${selectedDowngradeRoute.plan.label}`
            }
          </Button>
        </>
      )}
    </>
  );
};
